import { motion, AnimatePresence } from "framer-motion";
import {
  EMOTIONS,
  MASCOT_ANIMATIONS,
} from "../../constants/emotions";

// Pick a tempo that matches the emotion's spirit.
function pickDuration(state) {
  if (state === "thinking" || state === "waiting") return 1.2;
  if (state === "reply" || state === "cheering") return 0.7;
  if (state === "greet") return 1.4;
  if (state === "surprised" || state === "wow") return 0.9;
  if (state === "sleeping") return 4;
  if (state === "celebrating_admission") return 0.9;
  return 3.2;
}

/**
 * Mascot avatar — animated circle showing the AskPurple character in the
 * current emotional state.
 *
 * `variant`:
 *   - "compact" → small widget chrome (thin ring, soft shadow)
 *   - "large"   → full-page chat (thick white ring, big shadow)
 */
export default function MascotAvatar({
  state = "idle",
  size = 56,
  withFloat = true,
  variant = "compact",
}) {
  const src = EMOTIONS[state] || EMOTIONS.idle;
  const duration = pickDuration(state);

  const ringClass =
    variant === "large" ? "ring-4 ring-white/90" : "ring-2 ring-white/80";
  const shadowClass =
    variant === "large"
      ? "shadow-[0_8px_24px_rgba(168,85,247,0.55)]"
      : "shadow-[0_6px_18px_rgba(168,85,247,0.45)]";
  const haloClass =
    variant === "large"
      ? "absolute -inset-2 rounded-full bg-gradient-to-br from-fuchsia-400/40 via-purple-400/30 to-pink-400/40 blur-lg"
      : "absolute inset-0 rounded-full bg-gradient-to-br from-fuchsia-400/40 via-purple-400/30 to-pink-400/40 blur-md";

  return (
    <div
      className="relative inline-block overflow-visible"
      style={{ width: size, height: size }}
    >
      <span className={`pointer-events-none ${haloClass}`} aria-hidden />
      <motion.div
        className={`relative h-full w-full overflow-hidden rounded-full ${ringClass} bg-gradient-to-br from-purple-100 to-pink-100 ${shadowClass}`}
        animate={withFloat ? MASCOT_ANIMATIONS[state] : undefined}
        transition={{ duration, repeat: Infinity, ease: "easeInOut" }}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={src}
            src={src}
            alt={`AskPurple ${state}`}
            draggable={false}
            loading="eager"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 h-full w-full select-none object-cover"
          />
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
